import React, { useState, useEffect, ChangeEvent } from "react";
import { Errors, Field } from "../../../Interface/scheduler/form";
import {
  formatPhoneInputWithCursorPosition,
  formatPhoneNumber,
  getEmailValidationError,
  updateFormValuesWithCategoryBasedQAs,
  validateSquareFeet,
} from "../Helper/Helper";
import { getValidationError } from "../FieldValidator/validateFields";
import { fetchDynamicMenuJson } from "../../../service/apiService";
import { useSharedStateWithActions } from "../../hooks/useSharedState";
import "./FormBuilder.css";
import {
  FORM_VALUES_KEYS,
  NAME_CATEGORY_BASED_QA_PREFIX,
  sharedStateKeys,
  AppConstants,
  CONTACT_ADD_NOTE,
  MOBILE_ADD_NOTE,
} from "../../../constants/constant";
import { useFormContext } from "../../contexts/FormContext";
import { OptinCheck } from "../../../feature/scheduler/components/Helper/OptinCheck";
import IdentifyCountryCode from "../../../feature/scheduler/components/Helper/IdentifyCountryCode";

import { formBuilderFieldRendererMap } from "./formBuilderRenderMap";
interface FormBuilderProps {
  field: Field;
  errors: Errors;
  setErrors: React.Dispatch<React.SetStateAction<Errors>>;
  disabled?: boolean;
}

const FormBuilder: React.FC<FormBuilderProps> = ({
  field,
  errors,
  setErrors,
  disabled = false,
}) => {
  const [options, setOptions] = useState([]);

  const { formValues, setFormValues } = useFormContext();
  const { state, setValue, removeValue } = useSharedStateWithActions();

  useEffect(() => {
    OptinCheck(formValues, setFormValues);
  }, [formValues.zipcode, formValues.servicereminder]);

  useEffect(() => {
    const isServiceTypeField =
      field.id === AppConstants.FIELD_ID_SERVICE_TYPE ||
      field.id === AppConstants.FIELD_ID_PRO_TYPE ||
      field.subType?.toLowerCase() === AppConstants.FIELD_ID_SERVICE_TYPE;

    const shouldFetchDynamicMenu =
      isServiceTypeField && field?.options?.length > 0;

    const fetchDynamicMenuData = async () => {
      if (!formValues) return;

      try {
        const zipCode = formValues.zipcode;
        const countryCode = IdentifyCountryCode(zipCode);
        const data = await fetchDynamicMenuJson(
          formValues,
          state?.[sharedStateKeys.MULTIPLE_DOMAINS],
          countryCode,
        );

        if (data) {
          setOptions(data);
          setValue(sharedStateKeys.CES_DYNAMIC_MENU_API_DATA, data);
        }
      } catch (error) {
        console.error("Error fetching dynamic menu JSON:", error);
      }
    };

    if (shouldFetchDynamicMenu) {
      fetchDynamicMenuData();

      // Re-set form values (effectively a no-op unless used for triggering updates)
      setFormValues((prevValues) => ({ ...prevValues }));
    }

    if (field?.id === AppConstants.FIELD_ID_SERVICE) {
      setValue(sharedStateKeys.IS_SERVICE_SKU_FIELD_AVAILABLE, true);
    }
  }, []);

  const handleSquareFeetChange = (name: string, value: string) => {
    const squareFeet = validateSquareFeet(value);
    setFormValues((prevValues) => ({
      ...prevValues,
      [name]: squareFeet,
    }));
    setErrors((prevErrors) => ({
      ...prevErrors,
      [name]: getValidationError(field, value) || "",
    }));
  };

  const handleEmailBlur = async (e) => {
    const { name, value } = e.target;
    if (name === "email") {
      const emailError = (await getEmailValidationError(field, value)) || "";
      setErrors((prevErrors) => ({
        ...prevErrors,
        [name]: emailError ? emailError : prevErrors[name] || "",
        // Handle string or null errors
      }));
    }
  };
  const handleChange = (
    e: ChangeEvent<
      | HTMLInputElement
      | HTMLSelectElement
      | HTMLTextAreaElement
      | { name?: string; value: unknown }
    >,
  ): void => {
    const { name, value } = e.target;
    if (!name) return;
    if (name === "pricequoteselection") {
      const matchingRadioSelection = field.options?.find(
        (opt) => opt.name === value,
      );
      if (matchingRadioSelection?.onSelection) {
        setValue(
          sharedStateKeys.PRICE_QUOTE_SELECTION_DETAILS,
          matchingRadioSelection.onSelection,
        );
      } else {
        removeValue(sharedStateKeys.PRICE_QUOTE_SELECTION_DETAILS);
      }
    }

    if (name === AppConstants.MOBILE_ID_SELECTION) {
      if (value === "contact-address") {
        setFormValues((prevValues) => ({
          ...prevValues,
          [AppConstants.GLASS_SERVICE_TYPE]: CONTACT_ADD_NOTE,
        }));
      } else if (value === "different-address") {
        setFormValues((prevValues) => ({
          ...prevValues,
          [AppConstants.GLASS_SERVICE_TYPE]: MOBILE_ADD_NOTE,
        }));
      }
    }

    if (name === AppConstants.FIELD_ID_CAR_SERVICE_SELECTION) {
      const dropOffSelectionValue = field.options?.find(
        (opt) => opt.id === value,
      );

      if (value === AppConstants.DROPOFF_SERVICE_ID) {
        setFormValues((prev) => ({
          ...prev,
          dropOffLocationRadioLabelText: dropOffSelectionValue?.label || "",
          [AppConstants.GLASS_SERVICE_TYPE]: `${dropOffSelectionValue?.label || ""} ${prev["dbaName"]}`,
        }));
      }
    }

    if (name === "servicetype") {
      const serviceId = (e.target as HTMLElement).getAttribute(
        "data-serviceId",
      );
      const proptype = options?.find(
        (opt) => opt.cesname?.toLowerCase() === value,
      );
      sessionStorage.removeItem("categories");
      sessionStorage.setItem("categories", JSON.stringify(proptype));

      //Reset the category based questions array on change of service type
      //and remove it from session storage
      setFormValues((prev) => ({
        ...prev,
        proptype: proptype?.name,
        serviceTypeID: serviceId || "",
        [FORM_VALUES_KEYS.CATEGORY_BASED_QAS]: [],
        servicecategory: "",
        serviceName: "",
        service: "",
      }));
    }

    if (name === "protype" && value) {
      setFormValues((prev) => ({
        ...prev,
        proTypeName: value as string,
      }));
    }

    if (name === "square-feet" || name === "office-square-feet") {
      handleSquareFeetChange(name, value as string);
      return;
    }

    const categories = sessionStorage.getItem("categories");
    const serviceCategory =
      categories && categories !== "undefined" ? JSON.parse(categories) : null;
    if (name === "servicecategory" && serviceCategory) {
      const services = serviceCategory?.category?.find(
        (cat) => cat.id === parseInt(value as string),
      );
      if (services) {
        setFormValues((prev) => ({
          ...prev,
          serviceCategoryName: (
            e.target as HTMLSelectElement & { category?: string }
          )?.category,
          service: "",
        }));
      }
    }

    setErrors((prevErrors) => ({
      ...prevErrors,
      [name]: getValidationError(field, value as string) || "",
    }));

    if (name.startsWith(NAME_CATEGORY_BASED_QA_PREFIX)) {
      const inputId = (e.target as HTMLElement)?.id; // Get the ID of the input
      const question =
        document.querySelector(`label[for="${inputId}"]`)?.textContent || ""; // Find the corresponding label
      if (question) {
        setFormValues((prevValues) =>
          updateFormValuesWithCategoryBasedQAs(
            prevValues,
            name,
            value as string,
            question,
          ),
        );
      }
      return;
    } else {
      let formattedPhoneNumber = value;
      if (name === "phone") {
        const input = e.target as HTMLInputElement;
        formattedPhoneNumber = formatPhoneInputWithCursorPosition(
          input,
          value as string,
          formatPhoneNumber,
        );
      }
      setFormValues((prevValues) => ({
        ...prevValues,
        ...(name === "cleaning-service" && {
          servicetype: e.target.value,
        }),
        [name]: name === "phone" ? formattedPhoneNumber : (value as string),
        serviceName:
          name === "service"
            ? (e.target as HTMLSelectElement & { serviceName?: string })
                ?.serviceName
            : prevValues.serviceName,
      }));
    }
  };

  const handleCheckBoxGroupIcon = (name: string, val: string[]) => {
    setFormValues((prev) => ({
      ...prev,
      [name]: val,
    }));
    setErrors((prevErrors) => ({
      ...prevErrors,
      [name]: getValidationError(field, val) || "",
    }));
  };

  const handleAutoSelect = (name: string, val: string) => {
    setFormValues((prev) => ({
      ...prev,
      [name]: val,
    }));
    setErrors((prevErrors) => ({
      ...prevErrors,
      [name]: getValidationError(field, val) || "",
    }));
  };

  const renderField = (field: Field) => {
    const renderFn = formBuilderFieldRendererMap[field.type];
    return renderFn
      ? renderFn({
          field,
          error: errors[field.id],
          errors,
          setErrors,
          handleChange,
          handleCheckBoxGroupIcon,
          handleAutoSelect,
          options,
          handleEmailBlur,
          disabled,
        })
      : null;
  };

  return <>{renderField(field)}</>;
};

export default FormBuilder;
